import { z } from 'zod'
import type { ReactNode } from 'react'
import type { AnyFieldApi, ReactFormExtendedApi } from '@tanstack/react-form'
import { TextField, TextFieldSlot } from '../../TextField'
import { Label } from '../../Label'
import { Alert } from '../../Alert'
import { Flex } from '@radix-ui/themes'

/**
 * Catch-all TanStack Form React API type. `ReactFormExtendedApi` takes 12 generics
 * (form data shape + every validator slot); using `any` here matches the library's
 * own `AnyFieldApi`/`AnyFormApi` pattern so shared components accept any form.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyReactFormApi = ReactFormExtendedApi<any, any, any, any, any, any, any, any, any, any, any, any>

interface FormFieldProps {
  name: string
  label?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url'
  placeholder?: string
  autoComplete?: string
  required?: boolean
  disabled?: boolean
  className?: string
  prefix?: ReactNode
  validationSchema?: z.ZodSchema<unknown> | (() => z.ZodSchema<unknown>)
  children?: ReactNode
  form: AnyReactFormApi
}

export const FormField = ({
  name,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  required = false,
  disabled = false,
  className = '',
  prefix,
  validationSchema,
  children,
  form
}: FormFieldProps) => {

  // Create validators from Zod schema - validate on blur for better UX
  const validators = validationSchema ? {
    onBlur: ({ value }: { value: unknown }) => {
      const schema = typeof validationSchema === 'function' ? validationSchema() : validationSchema
      const result = schema.safeParse(value)
      if (!result.success) {
        return result.error.issues[0]?.message
      }
      return undefined
    }
  } : {}

  return (
    <form.Field
      name={name}
      validators={validators}
    >
      {(field: AnyFieldApi) => (
        <Flex direction="column" gap="2" className="w-full">
          {label && (
            <Label htmlFor={field.name}>
              {label}
              {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
          )}

          <TextField
            id={field.name}
            name={field.name}
            type={type}
            autoComplete={autoComplete}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            className={className}
            value={field.state.value as string}
            onChange={(e) => {
              const value = type === 'number'
                ? e.target.valueAsNumber
                : e.target.value
              field.handleChange(value)
            }}
            onBlur={field.handleBlur}
          >
            {prefix && (
              <TextFieldSlot side="left">
                <span className="text-sm text-gray-600 select-none">{prefix}</span>
                <div className="w-px h-5 bg-gray-300 -mr-1" />
              </TextFieldSlot>
            )}
          </TextField>

          {field.state.meta.errors.length > 0 && (
            <Alert variant="error">
              {field.state.meta.errors.join(', ')}
            </Alert>
          )}

          {children}
        </Flex>
      )}
    </form.Field>
  )
}
